import { useState, useEffect, useRef } from 'react';
import { Database, Search, FileText, Shield, CheckSquare, Phone } from 'lucide-react';

interface StepConfig {
  icon: React.ElementType;
  label: string;
  messages: string[];
}

const STEPS: StepConfig[] = [
  {
    icon: Database,
    label: 'Gathering Info',
    messages: [
      'Loading your agent settings...',
      'Fetching sales materials...',
      'Selecting phone number...',
    ],
  },
  {
    icon: Search,
    label: 'Studying Info',
    messages: [
      'Analyzing prospect data...',
      'Reviewing company profile...',
      'Preparing context...',
    ],
  },
  {
    icon: FileText,
    label: 'Writing a Call Script',
    messages: [
      'Crafting your opening line...',
      'Building conversation flow...',
      'Personalizing the pitch...',
    ],
  },
  {
    icon: Shield,
    label: 'Adding Objection Handling',
    messages: [
      'Preparing common responses...',
      'Adding fallback strategies...',
      'Fine-tuning objections...',
    ],
  },
  {
    icon: CheckSquare,
    label: 'Verifying Everything',
    messages: [
      'Running final checks...',
      'Checking calendar availability...',
      'Validating call configuration...',
    ],
  },
  {
    icon: Phone,
    label: 'Calling Now!',
    messages: [
      'Dialing your number...',
      'Connecting to phone network...',
      'Placing the call...',
    ],
  },
];

const STEP_INTERVAL_MS = 5000;
const MESSAGE_INTERVAL_MS = 2500;

interface CallProgressStepsProps {
  apiDone: boolean;
  apiFailed: boolean;
}

export function CallProgressSteps({ apiDone, apiFailed }: CallProgressStepsProps) {
  const [activeIndex, setActiveIndex] = useState(0);
  const [messageIndex, setMessageIndex] = useState(0);
  const [fading, setFading] = useState(false);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const completedViaApi = useRef(false);

  useEffect(() => {
    timerRef.current = setInterval(() => {
      setFading(true);
      setTimeout(() => {
        setActiveIndex(prev => {
          if (prev < STEPS.length - 1) return prev + 1;
          return prev;
        });
        setFading(false);
      }, 300);
    }, STEP_INTERVAL_MS);

    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, []);

  useEffect(() => {
    const interval = setInterval(() => setMessageIndex(prev => prev + 1), MESSAGE_INTERVAL_MS);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (apiDone && !completedViaApi.current) {
      completedViaApi.current = true;
      if (timerRef.current) clearInterval(timerRef.current);
      setFading(true);
      setTimeout(() => {
        setActiveIndex(STEPS.length - 1);
        setFading(false);
      }, 300);
    }
  }, [apiDone]);

  useEffect(() => {
    if (apiFailed) {
      if (timerRef.current) clearInterval(timerRef.current);
    }
  }, [apiFailed]);

  const step = STEPS[activeIndex];
  const Icon = step.icon;
  const currentMessage = step.messages[messageIndex % step.messages.length];

  return (
    <div className="py-6 flex flex-col items-center">
      <div className={`flex flex-col items-center transition-opacity duration-300 ${fading ? 'opacity-0' : 'opacity-100'}`}>
        <div className="relative w-14 h-14 rounded-2xl bg-gradient-to-br from-[#1875FF]/15 to-[#05E0D1]/15 flex items-center justify-center mb-4">
          <Icon className="w-6 h-6 text-[#1875FF] animate-[pulse_1.8s_ease-in-out_infinite]" />
          <div className="absolute inset-0 rounded-2xl bg-[#1875FF]/10 animate-ping" style={{ animationDuration: '2s' }} />
        </div>

        <p className="text-base font-semibold text-gray-900">{step.label}</p>
        <p className="text-sm text-gray-400 mt-1 h-5">{currentMessage}</p>
      </div>

      <div className="flex items-center gap-2 mt-8">
        {STEPS.map((_, i) => (
          <div
            key={i}
            className={`h-1.5 rounded-full transition-all duration-500 ${
              i < activeIndex
                ? 'w-1.5 bg-gradient-to-r from-[#1875FF] to-[#05E0D1]'
                : i === activeIndex
                  ? 'w-6 bg-gradient-to-r from-[#1875FF] to-[#05E0D1]'
                  : 'w-1.5 bg-gray-200'
            }`}
          />
        ))}
      </div>

      <p className="text-center text-xs text-gray-400 mt-4">
        Usually takes about 30 seconds
      </p>
    </div>
  );
}
